import { useState, useEffect } from "react";
import type { JSX } from "react";
import { Navigate } from "react-router-dom";
import {
  LockClosedIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import ButtonSpinner from "../components/ButtonSpinner";
import Tooltip from "../components/Tooltip";
import { useAuth } from "../contexts/AuthContext";
import { authApi } from "../services/authApi";

/**
 * Password strength scoring (simplified version of zxcvbn)
 * Returns score 0-4 where 3+ is acceptable
 */
const calculatePasswordStrength = (password: string): { score: number; feedback: string[] } => {
  const feedback: string[] = [];
  let score = 0;

  // Length check
  if (password.length >= 12) score += 1;
  else feedback.push("Use at least 12 characters");

  // Complexity checks
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  else feedback.push("Include both uppercase and lowercase letters");

  if (/\d/.test(password)) score += 1;
  else feedback.push("Include at least one number");

  if (/[^a-zA-Z0-9]/.test(password)) score += 1;
  else feedback.push("Include at least one special character");

  // Bonus for good patterns
  if (password.length >= 16) score += 0.5;
  if (!/(.)\1{2,}/.test(password)) score += 0.5; // No repeated characters

  return {
    score: Math.min(Math.floor(score), 4),
    feedback,
  };
};

export const MustResetPassword = (): JSX.Element => {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: [] as string[] });

  // Apply theme from localStorage on mount (same pattern as Layout.tsx)
  // MUST be before any conditional returns (Rules of Hooks)
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "dark") {
      document.documentElement.classList.add("dark");
      document.documentElement.classList.remove("light");
    } else if (savedTheme === "light") {
      document.documentElement.classList.remove("dark");
      document.documentElement.classList.add("light");
    } else {
      // No saved preference - check system preference
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      if (prefersDark) {
        document.documentElement.classList.add("dark");
      }
    }
  }, []);

  // Calculate password strength on new password change
  useEffect(() => {
    if (newPassword.length > 0) {
      setPasswordStrength(calculatePasswordStrength(newPassword));
    } else {
      setPasswordStrength({ score: 0, feedback: [] });
    }
  }, [newPassword]);

  // Redirect if not authenticated
  if (!isAuthenticated && !isLoading) {
    return <Navigate to="/login" replace />;
  }

  // Redirect if password change not required
  if (isAuthenticated && user !== null && !user.must_change_password) {
    return <Navigate to="/" replace />;
  }

  const getStrengthColor = (score: number): string => {
    if (score === 0) return "bg-gray-200 dark:bg-gray-700";
    if (score === 1) return "bg-red-500";
    if (score === 2) return "bg-orange-500";
    if (score === 3) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getStrengthLabel = (score: number): string => {
    if (score === 0) return "";
    if (score === 1) return "Weak";
    if (score === 2) return "Fair";
    if (score === 3) return "Good";
    return "Strong";
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError("");

    // Validation
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    if (newPassword.length < 12) {
      setError("Password must be at least 12 characters long");
      return;
    }

    if (passwordStrength.score < 3) {
      setError("Password is too weak. Please choose a stronger password.");
      return;
    }

    if (currentPassword === newPassword) {
      setError("New password must be different from current password");
      return;
    }

    setSubmitting(true);

    try {
      await authApi.changePassword(currentPassword, newPassword);
      setSuccess(true);

      // Logout after 2 seconds to force re-login with new password
      setTimeout(() => {
        void logout();
      }, 2000);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      const errorMessage =
        error.response?.data?.detail ?? "Password change failed. Please try again.";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <ButtonSpinner size="lg" className="text-blue-600" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-8 text-center">
            <CheckCircleIcon className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Password Changed Successfully
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Your password has been updated. You will be logged out shortly. Please log in again
              with your new password.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <ButtonSpinner size="sm" />
              Logging out...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <ShieldCheckIcon className="w-16 h-16 text-blue-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Password Reset Required
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            For security reasons, you must change your password before continuing.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-8">
          {/* Security Notice */}
          <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-start gap-3">
              <ExclamationCircleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium mb-1">
                  Password Requirements
                </p>
                <ul className="text-xs text-yellow-700 dark:text-yellow-300 list-disc list-inside space-y-1">
                  <li>At least 12 characters long</li>
                  <li>Mix of uppercase and lowercase letters</li>
                  <li>Include numbers and special characters</li>
                  <li>Different from your current password</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Error Alert */}
          {error.length > 0 && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <XCircleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-600 dark:text-red-400">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Current Password Field */}
            <div>
              <label
                htmlFor="currentPassword"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Current Password
              </label>
              <div className="relative">
                <LockClosedIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  id="currentPassword"
                  type="password"
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent
                           disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Enter your current password"
                  disabled={submitting}
                  autoComplete="current-password"
                  autoFocus
                />
              </div>
            </div>

            {/* New Password Field */}
            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                New Password
              </label>
              <div className="relative">
                <LockClosedIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  id="newPassword"
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent
                           disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Enter your new password"
                  disabled={submitting}
                  autoComplete="new-password"
                />
              </div>

              {/* Password Strength Indicator */}
              {newPassword.length > 0 && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[0, 1, 2, 3].map((index) => (
                      <div
                        key={index}
                        className={`h-1.5 flex-1 rounded-full transition-colors ${
                          index < passwordStrength.score
                            ? getStrengthColor(passwordStrength.score)
                            : "bg-gray-200 dark:bg-gray-700"
                        }`}
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {getStrengthLabel(passwordStrength.score)}
                    </span>
                    {passwordStrength.score < 3 && passwordStrength.feedback.length > 0 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {passwordStrength.feedback[0]}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Confirm New Password
              </label>
              <div className="relative">
                <LockClosedIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent
                           disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Confirm your new password"
                  disabled={submitting}
                  autoComplete="new-password"
                />
              </div>
              {confirmPassword.length > 0 && newPassword !== confirmPassword && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                  Passwords do not match
                </p>
              )}
            </div>

            {/* Submit Button */}
            <Tooltip
              content={
                !submitting && (passwordStrength.score < 3 || newPassword !== confirmPassword)
                  ? "Choose a stronger password and make both fields match"
                  : ""
              }
            >
              <button
                type="submit"
                disabled={
                  submitting || passwordStrength.score < 3 || newPassword !== confirmPassword
                }
                className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg
                       disabled:opacity-50 disabled:cursor-not-allowed
                       flex items-center justify-center gap-2
                       transition-colors duration-200"
              >
                {submitting ? (
                  <>
                    <ButtonSpinner size="md" />
                    Changing Password...
                  </>
                ) : (
                  <>
                    <ShieldCheckIcon className="w-5 h-5" />
                    Change Password
                  </>
                )}
              </button>
            </Tooltip>
          </form>

          {/* Cancel/Logout Option */}
          <div className="mt-6 text-center">
            <button
              onClick={() => void logout()}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline"
              disabled={submitting}
            >
              Cancel and log out
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
          <p>© 2025 Inventory Management System</p>
        </div>
      </div>
    </div>
  );
};
